import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { harnessService } from '@/services/api/harnessService';
import type { CurveParameters, MeasurementMetricsRow } from '@/features/harness/types';

interface ParamDef {
  key: keyof CurveParameters;
  label: string;
  unit?: string;
}

/** The nine absolute per-curve scalars (AC2), in display order. */
const PARAM_DEFS: ParamDef[] = [
  { key: 'maxImaginary', label: 'Max Imaginary', unit: 'Ω' },
  { key: 'maxImaginaryInterpolated', label: 'Max Imaginary Interpolated', unit: 'Ω' },
  { key: 'nyquistArea', label: 'Nyquist Area' },
  { key: 'freqArea', label: 'Freq Area' },
  { key: 'ascendingArcArea', label: 'Ascending Arc Area' },
  { key: 'curveRadius', label: 'Curve Radius', unit: 'Ω' },
  { key: 'hookReversalMagnitude', label: 'Hook Reversal Magnitude', unit: 'Ω' },
  { key: 'slopeReversalRatio', label: 'Slope Reversal Ratio' },
  { key: 'rsAt10kHz', label: 'Rs @ 10 kHz', unit: 'Ω' },
];

/** A null/NaN parameter is "not computable" (AC3/AC4) — render it as "—", never a misleading 0. */
function formatValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(3);
}

function paramLabel(def: ParamDef): string {
  return def.unit ? `${def.label} (${def.unit})` : def.label;
}

/** The 1-based channel keys present across the given rows, ascending. */
function unionChannelKeys(rows: MeasurementMetricsRow[]): string[] {
  const keys = new Set<string>();
  for (const row of rows) {
    for (const ch of Object.keys(row.parametersByChannel)) keys.add(ch);
  }
  return Array.from(keys).sort((a, b) => Number(a) - Number(b));
}

export interface CurveParametersViewProps {
  /** The Batch to compute metrics within (its id is passed to `selection-metrics`). */
  batchId: string | null | undefined;
  /** Selected Measurement ids in run order. */
  selectedIds: string[];
}

/**
 * Presentational core of the curve-parameters panel: fetches and renders the nine absolute curve
 * parameters (AC2/AC3/AC4) for the current selection via the backend `selection-metrics` endpoint
 * — never re-derived in the frontend. Reads nothing from the store; the live harness view and the
 * connection-free review view (Story 3.1) both pass their own `batchId`/`selectedIds`.
 *
 * With two or more Measurements selected (Story 2.1), it renders a comparison table: one row per
 * Measurement in SequenceNumber order (the order the backend returns), columns = the nine
 * parameters, plus a channel selector so per-channel values stay legible (AC3). With exactly one
 * selected it degrades to the Story 1.4 single-measurement panel (channels as columns).
 */
export function CurveParametersView({ batchId, selectedIds }: CurveParametersViewProps) {
  const [rows, setRows] = useState<MeasurementMetricsRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);

  // A stable dependency key: the array reference changes on every selection toggle, but this
  // guards against needless refetches if an unrelated update produced an equal selection.
  const idsKey = selectedIds.join(',');

  useEffect(() => {
    if (!batchId || selectedIds.length === 0) {
      setRows([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      // Clear the previous rows so a selection change never briefly shows stale values (AC5)
      // from the prior selection while the new fetch is in flight.
      setRows([]);
      setLoading(true);
      setError(null);
      try {
        const result = await harnessService.getSelectionMetrics(batchId, selectedIds);
        if (cancelled) return;
        setRows(result);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load curve parameters.');
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // idsKey stands in for selectedIds (a fresh array ref each toggle); listing both would be
    // redundant. batchId is the other input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, idsKey]);

  const channelKeys = useMemo(() => unionChannelKeys(rows), [rows]);

  // Keep the selected channel valid as the selection (and thus the available channels) changes,
  // without an extra effect: fall back to the first available channel.
  const effectiveChannel =
    selectedChannel && channelKeys.includes(selectedChannel) ? selectedChannel : channelKeys[0];

  if (selectedIds.length === 0 || !batchId) {
    return null;
  }

  const isComparison = selectedIds.length > 1;
  const noData = !loading && !error && rows.length > 0 && channelKeys.length === 0;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        {isComparison ? 'Comparison — Curve Parameters' : 'Curve Parameters'}
      </Typography>
      {loading && <CircularProgress size={20} />}
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      {noData && (
        <Typography variant="body2" color="text.secondary">
          No curve data available for {isComparison ? 'the selected Measurements' : 'this Measurement'}.
        </Typography>
      )}

      {!loading && !error && channelKeys.length > 0 && (
        <>
          {/* Channel selector — shown when a curve exposes more than one channel (AC3). In the
              single-measurement (N=1) view, channels are laid out as columns instead. */}
          {isComparison && channelKeys.length > 1 && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={effectiveChannel}
              onChange={(_, ch) => ch && setSelectedChannel(ch)}
              sx={{ mb: 1.5 }}
            >
              {channelKeys.map((ch) => (
                <ToggleButton key={ch} value={ch}>
                  CH{ch}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          <TableContainer sx={{ overflowX: 'auto' }}>
            {isComparison ? (
              // Comparison: one row per Measurement (SequenceNumber order), columns = the nine
              // parameters, values for the selected channel.
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Measurement</TableCell>
                    {PARAM_DEFS.map((def) => (
                      <TableCell key={def.key} align="right">
                        {paramLabel(def)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const params = row.parametersByChannel[effectiveChannel];
                    return (
                      <TableRow key={row.measurementId}>
                        <TableCell>#{row.sequenceNumber}</TableCell>
                        {PARAM_DEFS.map((def) => (
                          <TableCell key={def.key} align="right">
                            {formatValue(params?.[def.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              // Single measurement (N=1): the Story 1.4 layout — parameters as rows, channels as
              // columns.
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Parameter</TableCell>
                    {channelKeys.map((ch) => (
                      <TableCell key={ch} align="right">
                        CH{ch}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PARAM_DEFS.map((def) => (
                    <TableRow key={def.key}>
                      <TableCell>{paramLabel(def)}</TableCell>
                      {channelKeys.map((ch) => (
                        <TableCell key={ch} align="right">
                          {formatValue(rows[0].parametersByChannel[ch]?.[def.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </>
      )}
    </Box>
  );
}
